import { BellRing, FileText, Landmark } from "lucide-react";

import { StatusPill } from "@/components/portfolio/StatusPill";
import { Panel } from "@/components/ui/Panel";
import { cn } from "@/lib/utils";

type UpcomingPortfolioSummaryProps = {
  compact?: boolean;
  language?: "cs" | "en";
  items?: UpcomingPortfolioItem[];
  isLoading?: boolean;
};

export type UpcomingPortfolioItem = {
  id: string;
  date: string;
  title: string;
  value: string;
  type: "transaction" | "record";
  hasReminder?: boolean;
};

const summaryCopy = {
  cs: {
    eyebrow: "Přehled příštích 30 dní",
    description: "Nejbližší platby, termíny a nastavená připomenutí.",
    notification: "upozornění",
    loading: "Načítám transakce a záznamy…",
    empty: "V příštích 30 dnech nemáte žádnou transakci ani termín.",
    items: [
      {
        id: "mortgage",
        type: "transaction",
        hasReminder: false,
        date: "22. července",
        title: "Splátka hypotéky",
        value: "28 400 Kč",
      },
      {
        id: "water",
        type: "record",
        hasReminder: false,
        date: "31. července",
        title: "Odečet vody · Letná",
        value: "Připomenutí",
      },
      {
        id: "insurance",
        type: "record",
        hasReminder: true,
        date: "4. srpna",
        title: "Pojištění domu",
        value: "6 280 Kč",
      },
    ],
  },
  en: {
    eyebrow: "The next 30 days",
    description: "Upcoming payments, deadlines, and scheduled reminders.",
    notification: "notifications",
    loading: "Loading transactions and records…",
    empty: "You have no transaction or deadline in the next 30 days.",
    items: [
      {
        id: "mortgage",
        type: "transaction",
        hasReminder: false,
        date: "22 July",
        title: "Mortgage payment",
        value: "CZK 28,400",
      },
      {
        id: "water",
        type: "record",
        hasReminder: false,
        date: "31 July",
        title: "Water meter · Letná",
        value: "Reminder",
      },
      {
        id: "insurance",
        type: "record",
        hasReminder: true,
        date: "4 August",
        title: "Home insurance",
        value: "CZK 6,280",
      },
    ],
  },
} as const;

export function UpcomingPortfolioSummary({
  compact = false,
  language = "cs",
  items,
  isLoading = false,
}: UpcomingPortfolioSummaryProps) {
  const copy = summaryCopy[language];
  const displayedItems: UpcomingPortfolioItem[] = items ?? [...copy.items];
  const notificationCount = displayedItems.filter(
    (item) => item.type === "transaction" || item.hasReminder,
  ).length;

  const content = (
    <>
      {compact ? (
        <div className="app-section-divider-bottom flex flex-wrap items-start justify-between gap-3 px-5 py-4 sm:px-6">
          <div>
            <p className="app-section-eyebrow">{copy.eyebrow}</p>
            <p className="app-section-description mt-1">
              {copy.description}
            </p>
          </div>
          <StatusPill tone="warning" appearance="flat" className="gap-1.5">
            <BellRing className="size-3" aria-hidden />
            {notificationCount} {copy.notification}
          </StatusPill>
        </div>
      ) : (
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-softiq-secondary">
            {copy.eyebrow}
          </p>
        </div>
      )}

      <div
        className={cn(compact ? "px-5 sm:px-6" : "border-t border-white/15")}
      >
        {isLoading ? (
          <p className="py-7 text-sm text-softiq-primary/50 dark:text-white/50">
            {copy.loading}
          </p>
        ) : displayedItems.length === 0 ? (
          <p className="py-7 text-sm text-softiq-primary/50 dark:text-white/50">
            {copy.empty}
          </p>
        ) : (
          displayedItems.map((item) => {
            const ItemIcon =
              item.type === "transaction"
                ? Landmark
                : item.hasReminder
                  ? BellRing
                  : FileText;

            return (
              <div
                key={item.id}
                className={cn(
                  "grid grid-cols-[36px_1fr_auto] items-center gap-4 py-5 last:border-b-0",
                  compact
                    ? "border-b border-softiq-primary/10 dark:border-white/10"
                    : "border-b border-white/15",
                )}
              >
                <span
                  className={cn(
                    compact &&
                      "flex size-9 items-center justify-center rounded-lg bg-softiq-secondary-shade-100/80 text-softiq-primary dark:bg-white/10 dark:text-softiq-secondary",
                  )}
                >
                  <ItemIcon
                    className={cn(
                      "size-4",
                      compact ? "" : "text-softiq-secondary",
                    )}
                    aria-hidden
                  />
                </span>
                <div>
                  <p
                    className={cn(
                      "text-sm font-medium",
                      compact
                        ? "text-softiq-primary dark:text-white"
                        : "text-white",
                    )}
                  >
                    {item.title}
                  </p>
                  <p
                    className={cn(
                      "mt-1 text-xs",
                      compact
                        ? "text-softiq-primary/45 dark:text-white/45"
                        : "text-white/45",
                    )}
                  >
                    {item.date}
                  </p>
                </div>
                <p
                  className={cn(
                    "text-right text-xs",
                    compact
                      ? "text-softiq-primary/65 dark:text-white/65"
                      : "text-white/65",
                  )}
                >
                  {item.value}
                </p>
              </div>
            );
          })
        )}
      </div>
    </>
  );

  if (compact) {
    return (
      <Panel as="section" variant="section">
        {content}
      </Panel>
    );
  }

  return (
    <div className="grid gap-14 lg:grid-cols-[1fr_1fr]">
      {content}
    </div>
  );
}
